import {
  AccessControl,
  AlertPriority,
  AlertStatus,
  UNRESOLVED_ALERT_STATUSES,
} from '@pncl/common-models';
import { User } from './user';
import { Event } from './event';
import { SpaceRecordingSafeguardingInsights } from './space-recording';

/** These values must mirror the backend enum in packages/models/src/feature/alerts/models/alert.enums.ts */
export enum AlertEntityClass {
  EVENT = 'Event',
  SPACE_RECORDING_SAFEGUARDING_INSIGHTS = 'SpaceRecordingSafeguardingInsights',
  ANALYTICS_INSIGHT = 'AnalyticsInsight',
}

/** These values must mirror the backend enum in packages/models/src/feature/alerts/models/alert.enums.ts */
export enum AlertType {
  TEACHER_DECLINED_EVENT = 'TEACHER_DECLINED_EVENT',
  TEACHER_APPLICATION_PENDING = 'TEACHER_APPLICATION_PENDING',
  EVENT_NO_SHOW = 'EVENT_NO_SHOW',
  POST_SESSION_SAFEGUARDING = 'POST_SESSION_SAFEGUARDING',
  SESSION_NUDITY = 'SESSION_NUDITY',
}

/** These values must mirror the backend enum in packages/models/src/feature/alerts/models/alert.enums.ts */
export enum AlertChannel {
  UI = 'UI',
  EMAIL = 'EMAIL',
  SMS = 'SMS',
}

/**
 * Higher number = higher urgency. Shared with the backend through
 * `@pncl/common-models` rather than mirrored here, so `toAlertPriority` and
 * `PriorityConfig.id` are the same type.
 */
export { AlertPriority };

/**
 * Shared with the API through `@pncl/common-models`. `@pncl/models` keeps its
 * own copy for the mongoose schema.
 */
export { AlertStatus, UNRESOLVED_ALERT_STATUSES };

/** Single source of truth for priority display metadata — see AlertPriorityConfigService. */
export interface PriorityConfig {
  id: AlertPriority;
  label: string;
  icon: string;
}

/**
 * A rule as returned by GET /alerts/rules: the institution's override for an
 * alert type when one exists, otherwise the global default. Mirrors
 * AlertRuleClass in packages/models.
 *
 * PUT /alerts/rules expects the whole object back, not a partial patch — a rule
 * sent without `channels` or `visibleTo` creates an override that falls back to
 * schema defaults and silently stops that institution's alert emails.
 */
export interface AlertRule {
  _id: string;
  alertType: AlertType;
  /** null on a global default rule. */
  institution: string | null;
  defaultPriority: AlertPriority;
  enabled: boolean;
  channels: AlertChannel[];
  visibleTo: string[];
  capability?: AccessControl.Capability;
  /**
   * Institution setting this rule depends on. While it is off, the rule's row is
   * disabled in the Alerts settings panel.
   */
  institutionSettingKey?: string;
  expiryDurationMinutes?: number | null;
  createdAt?: string;
  updatedAt?: string;
}

/** Populated user fields returned by the API for triggeredBy / assignedTo. */
export type AlertUser = Pick<User, '_id' | 'name' | 'email' | 'profile_image'>;

/** Populated Event document on event-based alerts. */
export type EventAlertEntity = Partial<Event>;

/**
 * Space fields the alerts API nested-populates onto a space-scoped entity's
 * `spaceId`. Mirrors `ALERT_SPACE_POPULATE` in packages/models, which selects
 * `site title users._id users.isOwner`.
 */
export interface AlertSpace {
  _id: string;
  title?: string;
  site?: string;
  /** `_id` is null when the user was hard-deleted and the ref no longer resolves. */
  users?: { _id: AlertUser | null; isOwner?: boolean }[];
}

/** The contract RelatedSpaceComponent needs: an entity scoped to a space. */
export interface SpaceAlertEntity {
  /** Null once the space is deleted: spaces are hard-deleted, so the ref no longer resolves. */
  spaceId?: AlertSpace | null;
}

/**
 * SpaceRecordingSafeguardingInsights document on safeguarding alerts.
 *
 * `spaceId` is populated by GET /alerts but not by PATCH, which is why
 * `updateAlertAndSyncState` carries the entity it already holds.
 */
export type SafeguardingInsightsAlertEntity = Partial<
  Omit<SpaceRecordingSafeguardingInsights, 'spaceId'>
> &
  SpaceAlertEntity;

/**
 * AnalyticsInsight document on nudity alerts. Declares only the fields the
 * panel reads — the API returns the whole session analytics document.
 */
export type AnalyticsInsightAlertEntity = SpaceAlertEntity;

export type AlertEntity =
  | EventAlertEntity
  | SafeguardingInsightsAlertEntity
  | AnalyticsInsightAlertEntity;

/**
 * Alert document as returned by GET /alerts (with populated refs).
 */
export interface Alert<TEntity extends AlertEntity> {
  _id: string;
  alertType: AlertType;
  status: AlertStatus;
  institution: string;
  site?: string;
  entity: TEntity;
  entityClass: AlertEntityClass;
  priority: number;
  expiresAt?: Date | string | null;
  channels: AlertChannel[];
  visibleTo: string[];
  triggeredBy?: AlertUser | string;
  assignedTo?: AlertUser | string;
  resolution?: {
    closedAt?: string;
    closedBy?: string;
    closureReason?: string;
  };
  createdAt: string;
  updatedAt: string;
}
